import React, { useState } from 'react';
import './FAQ.css';

const FAQ = () => {
  const [openItems, setOpenItems] = useState(new Set([0])); // First item open by default

  const toggleItem = (index) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  const faqData = [
    {
      category: "General",
      questions: [
        {
          question: "Is the PDF converter and page remover free to use?",
          answer: "Yes, both tools are completely free to use with no hidden charges, subscriptions, or registration required. You can convert unlimited files and remove pages from PDFs without any cost."
        },
        {
          question: "Are my files uploaded to your servers?",
          answer: "No, absolutely not. All file processing happens directly in your browser using client-side JavaScript. Your files never leave your device, ensuring complete privacy and security. We cannot see, store, or access your files in any way."
        },
        {
          question: "Do I need to create an account or register?",
          answer: "No registration is required. Simply visit the website and start using the tools immediately. No email, personal information, or account creation needed."
        }
      ]
    },
    {
      category: "PDF Converter",
      questions: [
        {
          question: "What file formats can I convert to PDF?",
          answer: "Our PDF converter supports 15+ file formats including: Images (JPG, PNG, GIF, BMP, TIFF, WebP), Documents (DOC, DOCX), Spreadsheets (XLS, XLSX), Presentations (PPT, PPTX), Text files (TXT), Web files (HTML), and Markdown (MD) files."
        },
        {
          question: "What is the maximum file size I can convert?",
          answer: "The maximum file size is 10MB per file. You can convert multiple files in a single batch operation, and each file can be up to 10MB in size."
        },
        {
          question: "Can I merge multiple files into one PDF?",
          answer: "Yes! You can select multiple files and choose the merge option to combine them into a single PDF document. Alternatively, you can convert each file to its own separate PDF."
        },
        {
          question: "Can I adjust the PDF compression and quality?",
          answer: "Yes, our converter includes a compression slider (0-100%) that lets you balance file size and quality. Higher values create smaller files, while lower values preserve maximum quality."
        },
        {
          question: "How long does the conversion process take?",
          answer: "Conversion is typically very fast, usually completing within seconds to a minute depending on the file size and type. Since processing happens in your browser, speed depends on your device's performance."
        }
      ]
    },
    {
      category: "PDF Page Remover",
      questions: [
        {
          question: "What types of page removal operations can I perform?",
          answer: "You can: Remove specific page ranges (e.g., pages 3-7), Remove individual pages by number (e.g., pages 1,3,5), Keep only specific pages (remove all others), Remove first/last pages, and Remove custom page selections."
        },
        {
          question: "Can I preview pages before removing them?",
          answer: "Yes! Our page remover includes a preview feature that shows you the pages in your PDF, making it easy to identify exactly which pages you want to remove or keep."
        },
        {
          question: "Is there a limit on PDF file size for page removal?",
          answer: "The page remover can handle PDF files up to 50MB in size. For larger files, consider splitting them first or using the converter for smaller batches."
        },
        {
          question: "Will the page removal affect PDF quality?",
          answer: "No, page removal is a lossless operation. The remaining pages maintain their original quality, formatting, and all embedded content including images, links, and metadata."
        }
      ]
    },
    {
      category: "Technical & Security",
      questions: [
        {
          question: "Which browsers are supported?",
          answer: "Our tools work on all modern browsers including Chrome, Firefox, Safari, Edge, and Opera. JavaScript must be enabled for the tools to function properly."
        },
        {
          question: "Do you store any data about my conversions?",
          answer: "We do not store any file content, personal data, or conversion history. We may collect anonymous analytics data (like page views) to improve our service, but no personal or file information is tracked."
        },
        {
          question: "Is my data secure and private?",
          answer: "Yes, your data is completely secure because it never leaves your browser. All processing happens locally on your device. We follow industry best practices for web security and comply with privacy regulations including GDPR and DPDP Act 2023."
        },
        {
          question: "What happens if the conversion or page removal fails?",
          answer: "If an error occurs, you'll see a clear error message explaining the issue. Common causes include unsupported file formats, corrupted files, or files that exceed size limits. You can retry the operation or contact us for support."
        },
        {
          question: "Can I use these tools on mobile devices?",
          answer: "Yes, both tools are fully responsive and work on smartphones and tablets. However, for the best experience with large files or batch operations, we recommend using a desktop or laptop computer."
        }
      ]
    },
    {
      category: "Troubleshooting",
      questions: [
        {
          question: "Why is my file conversion taking a long time?",
          answer: "Large files or complex documents may take longer to process. Ensure you have a stable internet connection and sufficient device memory. If the process seems stuck, try refreshing the page and retrying with a smaller file."
        },
        {
          question: "The converted PDF looks different from my original file. Why?",
          answer: "Some formatting differences may occur during conversion, especially with complex layouts. For best results, use standard fonts and avoid heavily formatted documents. You can adjust compression settings to improve quality."
        },
        {
          question: "I'm getting an 'unsupported format' error. What should I do?",
          answer: "Check that your file is in one of our supported formats. If you believe it should be supported, try saving/exporting the file in a different format (e.g., save a Word doc as DOCX) and try again."
        },
        {
          question: "How can I report a bug or request a feature?",
          answer: "Use our feedback form to report bugs, request features, or share suggestions. We actively monitor feedback and regularly update our tools based on user input."
        }
      ]
    }
  ];

  let questionIndex = 0;

  return (
    <section id="faq" className="faq-section">
      <div className="landing-container-full">
        <div className="section-header">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <p className="section-description">
            Everything you need to know about our PDF converter and page remover tools
          </p>
        </div>

        <div className="faq-container">
          {faqData.map((category, categoryIndex) => (
            <div key={categoryIndex} className="faq-category">
              <div className="faq-category-header">
                <div className="category-icon">
                  {category.category === "General" && (
                    <svg width="24" height="24" viewBox="0 0 24 24">
                      <defs>
                        <linearGradient id="generalGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#667eea" />
                          <stop offset="100%" stopColor="#764ba2" />
                        </linearGradient>
                      </defs>
                      <circle cx="12" cy="12" r="10" stroke="url(#generalGradient)" strokeWidth="2" fill="none"/>
                      <path d="M9,9h6v6H9V9z" fill="url(#generalGradient)" fillOpacity="0.3"/>
                      <circle cx="12" cy="8" r="1" fill="url(#generalGradient)"/>
                      <path d="M12,10v6" stroke="url(#generalGradient)" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  )}
                  {category.category === "PDF Converter" && (
                    <svg width="24" height="24" viewBox="0 0 24 24">
                      <defs>
                        <linearGradient id="converterGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#4facfe" />
                          <stop offset="100%" stopColor="#00f2fe" />
                        </linearGradient>
                      </defs>
                      <rect x="4" y="3" width="12" height="16" rx="1" stroke="url(#converterGradient)" strokeWidth="2" fill="none"/>
                      <path d="M16 7l-4-4v4h4z" fill="url(#converterGradient)" fillOpacity="0.6"/>
                      <path d="M18 12h2m0 0l-2-2m2 2l-2 2" stroke="url(#converterGradient)" strokeWidth="2" strokeLinecap="round"/>
                      <rect x="20" y="9" width="2" height="6" fill="url(#converterGradient)"/>
                    </svg>
                  )}
                  {category.category === "PDF Page Remover" && (
                    <svg width="24" height="24" viewBox="0 0 24 24">
                      <defs>
                        <linearGradient id="removerGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#fa709a" />
                          <stop offset="100%" stopColor="#fee140" />
                        </linearGradient>
                      </defs>
                      <rect x="4" y="3" width="12" height="16" rx="1" stroke="url(#removerGradient)" strokeWidth="2" fill="none"/>
                      <path d="M6 7h8M6 10h8M6 13h6" stroke="url(#removerGradient)" strokeWidth="1.5" strokeLinecap="round"/>
                      <circle cx="19" cy="8" r="3" fill="url(#removerGradient)"/>
                      <path d="M17.5 8h3" stroke="white" strokeWidth="1.5"/>
                    </svg>
                  )}
                  {category.category === "Technical & Security" && (
                    <svg width="24" height="24" viewBox="0 0 24 24">
                      <defs>
                        <linearGradient id="securityGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#a8edea" />
                          <stop offset="100%" stopColor="#fed6e3" />
                        </linearGradient>
                      </defs>
                      <path d="M12 2l9 3v6c0 5.55-3.84 10.74-9 12-5.16-1.26-9-6.45-9-12V5l9-3z" stroke="url(#securityGradient)" strokeWidth="2" fill="none"/>
                      <path d="M9 12l2 2 4-4" stroke="url(#securityGradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                  {category.category === "Troubleshooting" && (
                    <svg width="24" height="24" viewBox="0 0 24 24">
                      <defs>
                        <linearGradient id="troubleshootGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#ffecd2" />
                          <stop offset="100%" stopColor="#fcb69f" />
                        </linearGradient>
                      </defs>
                      <circle cx="12" cy="12" r="9" stroke="url(#troubleshootGradient)" strokeWidth="2" fill="none"/>
                      <path d="M12 8v4M12 16h.01" stroke="url(#troubleshootGradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
                <h3 className="category-title">{category.category}</h3>
              </div>

              <div className="faq-items">
                {category.questions.map((item, itemIndex) => {
                  const currentIndex = questionIndex++;
                  const isOpen = openItems.has(currentIndex);

                  return (
                    <div key={itemIndex} className={`faq-item ${isOpen ? 'open' : ''}`}>
                      <button
                        className="faq-question"
                        onClick={() => toggleItem(currentIndex)}
                        aria-expanded={isOpen}
                      >
                        <span className="question-text">{item.question}</span>
                        <div className="faq-icon">
                          <svg width="20" height="20" viewBox="0 0 20 20">
                            <path
                              d="M5 8l5 5 5-5"
                              stroke="currentColor"
                              strokeWidth="2"
                              fill="none"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              style={{
                                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.3s ease'
                              }}
                            />
                          </svg>
                        </div>
                      </button>
                      <div className={`faq-answer ${isOpen ? 'open' : ''}`}>
                        <div className="answer-content">
                          <p>{item.answer}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="faq-footer">
          <div className="faq-footer-content">
            <h3>Still have questions?</h3>
            <p>Can't find the answer you're looking for? We're here to help!</p>
            <div className="faq-footer-buttons">
              <button
                className="btn btn-primary"
                onClick={() => window.location.hash = '#feedback'}
              >
                Contact Support
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => window.location.hash = '#converter'}
              >
                Try Our Tools
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;